const RAW_TO_STANDARD_SECTOR = {
  aerospace: "Aerospace & Defense",
  "aerospace & defense": "Aerospace & Defense",
  "aerospace and defense": "Aerospace & Defense",
  aerospace_defense: "Aerospace & Defense",
  "aviation & aerospace": "Aerospace & Defense",
  "aviation and aerospace": "Aerospace & Defense",
  "defense & space": "Aerospace & Defense",
  "defense and space": "Aerospace & Defense",
  agriculture: "Agriculture",
  "agriculture and forestry": "Agriculture",
  automotive: "Automotive",
  construction: "Construction",
  "construction and building materials": "Construction",
  "building materials": "Construction",
  corporate: "Corporate Services",
  "corporate services": "Corporate Services",
  "holding companies": "Corporate Services",
  "management of companies and enterprises": "Corporate Services",
  distribution: "Distribution",
  education: "Education",
  "educational services": "Education",
  school: "Education",
  schools: "Education",
  "schools and education": "Education",
  university: "Education",
  universities: "Education",
  "colleges and universities": "Education",
  "public school": "Education",
  "public schools": "Education",
  "public university": "Education",
  "local government school": "Education",
  "state government school": "Education",
  electronics: "Technology",
  energy: "Energy",
  "energy and environmental": "Energy",
  "clean energy and environment": "Energy",
  finance: "Financial Services",
  "finance and insurance": "Financial Services",
  "financial services": "Financial Services",
  banks: "Financial Services",
  insurance: "Financial Services",
  food: "Food & Beverage",
  beverage: "Food & Beverage",
  "food & beverage": "Food & Beverage",
  "food and beverage": "Food & Beverage",
  government: "Government",
  "public administration": "Government",
  healthcare: "Healthcare",
  "health care": "Healthcare",
  "health care and social assistance": "Healthcare",
  "hospital & health care": "Healthcare",
  "hospital and health care": "Healthcare",
  "hospitality and tourism": "Hospitality",
  hospitality: "Hospitality",
  "leisure, sports and recreation": "Hospitality",
  "travel and tourism": "Hospitality",
  "life sciences": "Life Sciences",
  "pharmaceuticals and biotechnology": "Life Sciences",
  biotechnology: "Life Sciences",
  logistics: "Logistics",
  "supply chain": "Logistics",
  manufacturing: "Manufacturing",
  "industrial manufacturing and services": "Manufacturing",
  chemicals: "Manufacturing",
  wood: "Manufacturing",
  paper: "Manufacturing",
  nonprofit: "Nonprofit",
  "non-profit": "Nonprofit",
  "non profit": "Nonprofit",
  "non-profit organization management": "Nonprofit",
  "grantmaking and giving services": "Nonprofit",
  "civic and social organizations": "Nonprofit",
  "religious organizations": "Nonprofit",
  "social advocacy organizations": "Nonprofit",
  "voluntary health organizations": "Nonprofit",
  "community housing services": "Nonprofit",
  "professional services": "Professional Services",
  "professional, scientific, and technical services": "Professional Services",
  consulting: "Professional Services",
  engineering: "Professional Services",
  "real estate": "Real Estate",
  "real estate and rental and leasing": "Real Estate",
  retail: "Retail",
  "retail trade": "Retail",
  technology: "Technology",
  information: "Technology",
  "computer hardware": "Technology",
  "computer software": "Technology",
  telecommunications: "Telecommunications",
  transportation: "Transportation",
  "transportation and warehousing": "Transportation",
  utilities: "Utilities",
  wholesale: "Wholesale",
};

const STANDARD_SECTOR_KEYWORDS = {
  "Aerospace & Defense": ["aerospace", "aviation", "defense", "military", "space"],
  Agriculture: ["agriculture", "farm", "farming", "fishery", "forestry"],
  Automotive: ["automotive", "vehicle", "vehicles"],
  Construction: ["architecture", "building", "construction", "contractor", "contracting"],
  "Corporate Services": ["corporate", "headquarters", "holding", "management"],
  Distribution: ["distribution", "distributor", "fulfillment"],
  Education: ["academy", "college", "education", "educational", "school", "schools", "training", "universities", "university"],
  Energy: ["clean energy", "energy", "environmental", "power", "renewable", "solar", "wind"],
  "Financial Services": ["bank", "banking", "finance", "financial", "fintech", "insurance"],
  "Food & Beverage": ["beverage", "food", "grocery", "restaurant"],
  Government: ["federal", "government", "municipal", "public administration", "public sector", "state agency"],
  Healthcare: ["care", "health", "healthcare", "hospital", "medical", "physician", "wellness"],
  Hospitality: ["accommodation", "casino", "hospitality", "hotel", "leisure", "lodging", "tourism", "travel"],
  "Life Sciences": ["biotech", "biotechnology", "laboratory", "life science", "life sciences", "pharma", "pharmaceutical"],
  Logistics: ["delivery", "freight", "logistics", "supply chain", "warehouse", "warehousing"],
  Manufacturing: ["chemical", "fabrication", "industrial", "machinery", "manufacturing", "plant", "printing"],
  Nonprofit: ["charity", "charitable", "community", "foundation", "non profit", "non-profit", "nonprofit"],
  "Professional Services": ["accounting", "consulting", "engineering", "legal", "professional", "scientific", "technical"],
  "Real Estate": ["property", "real estate"],
  Retail: ["apparel", "consumer", "e-commerce", "ecommerce", "retail", "shopping"],
  Technology: ["computer", "cybersecurity", "data", "electronics", "information", "internet", "software", "technology"],
  Telecommunications: ["telecom", "telecommunications", "wireless", "wired"],
  Transportation: ["air transportation", "transportation", "trucking"],
  Utilities: ["utilities", "utility", "water"],
  Wholesale: ["wholesale"],
};

const normalizeWhitespace = (value) =>
  String(value ?? "")
    .replace(/\u2013|\u2014|\u2212/g, "-")
    .replace(/[|/]+/g, ", ")
    .replace(/\s*&\s*/g, " and ")
    .replace(/\s+/g, " ")
    .trim();

const toLookupKey = (value) =>
  normalizeWhitespace(value)
    .toLowerCase()
    .replace(/,/g, " ")
    .replace(/-/g, " ")
    .replace(/\band\b/g, "and")
    .replace(/[()'.]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const titleCaseToken = (token) =>
  token ? token.charAt(0).toUpperCase() + token.slice(1).toLowerCase() : token;

const cleanSectorText = (value) => {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return "";

  return normalized
    .split(" ")
    .map((token) => (token === "&" ? token : titleCaseToken(token)))
    .join(" ");
};

const matchByKeywords = (lookupKey) => {
  let bestMatch = "";
  let bestScore = 0;

  Object.entries(STANDARD_SECTOR_KEYWORDS).forEach(([sector, keywords]) => {
    const score = keywords.reduce(
      (count, keyword) => (lookupKey.includes(keyword) ? count + 1 : count),
      0,
    );

    if (score > bestScore) {
      bestScore = score;
      bestMatch = sector;
    }
  });

  return bestScore > 0 ? bestMatch : "";
};

// Display aliases for existing labels only. Dropdown options come from
// useNaicsSectors and the backend NAICS reference table.
export const formatSectorLabel = (rawSector) => {
  const cleaned = cleanSectorText(rawSector);
  if (!cleaned) return "";

  const lookupKey = toLookupKey(cleaned);
  return RAW_TO_STANDARD_SECTOR[lookupKey] || matchByKeywords(lookupKey) || cleaned;
};

export const getSectorDisplayParts = (rawSector) => {
  const cleanedRawSector = cleanSectorText(rawSector);
  const standardizedSector = formatSectorLabel(rawSector);
  const showRawSector =
    Boolean(cleanedRawSector) &&
    Boolean(standardizedSector) &&
    cleanedRawSector.toLowerCase() !== standardizedSector.toLowerCase();

  return {
    standardizedSector,
    rawSector: cleanedRawSector,
    showRawSector,
  };
};
